import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star, MapPin, Users, Settings as SettingsIcon, ArrowLeft, Globe2, CheckCircle2, Phone, Pencil, CreditCard, AlertTriangle } from "lucide-react";
import { API, useAuth } from "../auth/AuthContext";
import SplitDateTime from "../components/SplitDateTime";
import KycModal from "../components/KycModal";
import TravelPackToggle from "../components/TravelPackToggle";
import EsimCard from "../components/EsimCard";
import { parseDateTimeLocal, formatTripRange, clientTimeZone } from "../lib/dateLocal";
import { setReturnTo } from "../lib/returnTo";
import { formatMoney, partnerCurrency, partnerSubtotal, partnerTripDays, partnerTripTotal } from "../lib/partnerPricing";
import { formatUsd } from "../lib/currencyDisplay";
import { listingCoverImage } from "../lib/fileUrl";
import { pageItems } from "../lib/pageItems";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { formatBookingError } from "../lib/bookingErrors";

/**
 * ExternalDetails — partner-fleet read view + Stripe checkout (same flow as hosted cars).
 *
 * Pricing comes from the partner search API (daily_rate + trip_total for the
 * quoted rental period). When Stripe is configured, payment goes through hosted
 * checkout before the booking is confirmed.
 *
 * Data source for the listing: passed via React Router `location.state.car`
 * from the Browse page. If a user lands here without state (refresh / shared
 * link) we show a friendly "re-run search" fallback.
 */
export default function ExternalDetails() {
  const { id } = useParams();
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const v = state?.car;

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pickupLoc, setPickupLoc] = useState("");
  const [oneWay, setOneWay] = useState(false);
  const [dropoffLoc, setDropoffLoc] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [addEsim, setAddEsim] = useState(true);
  const [airaloReady, setAiraloReady] = useState(null);
  const [confirmedEsim, setConfirmedEsim] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(params.get("stripe_cancel") === "1");

  useEffect(() => {
    if (v) setPickupLoc((v.location_address || v.location_city || "").trim());
  }, [v]);

  useEffect(() => {
    axios.get(`${API}/esim/airalo/status`)
      .then((r) => {
        const ok = !!r.data?.configured;
        setAiraloReady(ok);
        if (!ok) setAddEsim(false);
      })
      .catch(() => { setAiraloReady(false); setAddEsim(false); });
  }, []);

  useEffect(() => {
    axios.get(`${API}/payments/stripe/config`)
      .then((r) => setStripeConfigured(!!r.data?.configured))
      .catch(() => setStripeConfigured(false));
  }, []);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || params.get("stripe_cancel") === "1") return;
    navigate(`/external/${id}/complete?session_id=${encodeURIComponent(sessionId)}`, { replace: true });
  }, [id, params, navigate]);

  useEffect(() => {
    if (params.get("stripe_cancel") !== "1") return;
    const sessionId = params.get("session_id");
    if (sessionId) {
      axios.post(`${API}/payments/stripe/cancel`, { session_id: sessionId }, { withCredentials: true }).catch(() => {});
    }
    toast.message("Payment cancelled — you can try Stripe Checkout again");
    const next = new URLSearchParams(params);
    next.delete("stripe_cancel");
    next.delete("session_id");
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => {
    const s = parseDateTimeLocal(start);
    const e = parseDateTimeLocal(end);
    if (!s || !e || e <= s) return 0;
    const hours = (e - s) / 3600000;
    return Math.max(1, Math.ceil(hours / 24));
  }, [start, end]);

  if (!v) {
    return (
      <div className="max-w-[820px] mx-auto px-6 py-20" data-testid="ext-fallback">
        <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm text-[var(--zc-text-3)] hover:text-[var(--zc-text)] mb-6">
          <ArrowLeft size={14} /> Back to fleet
        </Link>
        <div className="zc-card p-10 text-center">
          <div className="w-12 h-12 mx-auto grid place-items-center rounded-full" style={{ background: "var(--zc-secondary-soft)", color: "var(--zc-secondary)" }}>
            <Globe2 size={20} />
          </div>
          <h1 className="zc-headline text-3xl mt-5">Partner listing not loaded.</h1>
          <p className="text-sm text-[var(--zc-text-2)] mt-3 max-w-sm mx-auto">
            Partner inventory is fetched live every time you search.
            Re-run your search to see this listing again.
          </p>
          <Link to="/browse" className="inline-flex items-center px-5 h-11 zc-btn-primary text-sm mt-6" data-testid="ext-back-cta">
            Browse the fleet
          </Link>
          <div className="zc-hint mt-3 font-mono">id: {id}</div>
        </div>
      </div>
    );
  }

  const currency = v ? partnerCurrency(v) : "USD";
  const subtotal = v ? partnerSubtotal(v, days) : 0;
  const oneWayFee = oneWay && (dropoffLoc || "").trim() ? 30 : 0;
  const total = Math.max(0, subtotal + oneWayFee);
  const apiTripTotal = v ? partnerTripTotal(v) : null;
  const apiTripDays = v ? partnerTripDays(v) : null;
  const img = listingCoverImage(v?.images);

  const isTraveloProPartner = (source) => {
    const s = (source || "").toLowerCase();
    return s === "travelopro" || s === "z-cars partner";
  };

  const partnerCheckoutErrorMessage = (detail) => {
    switch (detail) {
      case "travelopro_agency_card_not_configured":
        return "TraveloPro checkout is not fully configured on the server. Please contact support.";
      case "travelopro_requires_stripe_checkout":
        return "This partner vehicle must be paid through Stripe — test-card booking is not available.";
      case "stripe_not_configured":
        return "Stripe is not configured in this environment.";
      case "Vehicle is not available":
        return formatBookingError("Vehicle is not available");
      default:
        return typeof detail === "string" ? detail : "Could not place reservation";
    }
  };

  const bookingPayload = () => ({
    external_id: v.vehicle_id,
    external_source: v.external_source || "BookCars",
    snapshot: {
      make: v.make, model: v.model, year: v.year,
      images: v.images, image: img,
      location_city: v.location_city, daily_rate: v.daily_rate,
      owner_name: v.owner_name, seats: v.seats, transmission: v.transmission,
      currency: partnerCurrency(v),
      rate_total: partnerTripTotal(v),
      trip_total: partnerTripTotal(v),
      trip_days: partnerTripDays(v),
      partner_meta: v.partner_meta || null,
    },
    start_date: start,
    end_date: end,
    pickup_location: pickupLoc.trim(),
    dropoff_location: oneWay ? dropoffLoc.trim() : pickupLoc.trim(),
    one_way: oneWay && !!dropoffLoc.trim(),
    contact_phone: phone.trim() || null,
    notes: notes.trim() || null,
    add_esim: addEsim,
    client_tz: clientTimeZone(),
  });

  const isBookingValidationError = (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    if (status === 403 && detail === "kyc_required") return true;
    if (status === 403 && detail === "profile_photo_required") return true;
    if (status === 409 && detail === "Vehicle is not available") return true;
    if (status === 400) return true;
    return false;
  };

  const completeMockBooking = async () => {
    const res = await axios.post(`${API}/bookings/external`, bookingPayload(), { withCredentials: true });
    setConfirmed(res.data);
    if (res.data?.esim) setConfirmedEsim(res.data.esim);
    toast.success("Booking confirmed");
  };

  const submit = async () => {
    if (submitting) return;
    if (!user) {
      // Persist intent so post-login flows back to this page
      setReturnTo(window.location.pathname + window.location.search);
      toast.message("Sign in to reserve this partner vehicle");
      navigate("/login", { state: { authRequired: true } });
      return;
    }
    if (!start || !end) { toast.error("Pick dates first"); return; }
    if (days < 1) { toast.error("End date must be after start"); return; }
    if (!(pickupLoc || "").trim()) { toast.error("Enter a pick-up location"); return; }
    if (oneWay && !(dropoffLoc || "").trim()) { toast.error("Enter a drop-off location"); return; }
    setSubmitting(true);
    const traveloPro = isTraveloProPartner(v.external_source);
    try {
      const payload = bookingPayload();
      const tryStripe = stripeConfigured && total >= 0.5;

      if (tryStripe) {
        try {
          const sr = await axios.post(`${API}/payments/stripe/external-checkout-session`, payload, { withCredentials: true });
          if (sr.data?.url) {
            window.location.assign(sr.data.url);
            return;
          }
        } catch (stripeErr) {
          if (isBookingValidationError(stripeErr)) throw stripeErr;
          // TraveloPro requires Stripe when configured — never fall back to mock checkout.
          if (traveloPro) throw stripeErr;
          toast.message("Stripe unavailable — completing with card on file flow");
        }
      }

      await completeMockBooking();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail === "kyc_required") {
        setKycOpen(true);
      } else if (e.response?.status === 403 && detail === "profile_photo_required") {
        toast.error(formatBookingError(detail));
        navigate("/profile/settings");
      } else if (e.response?.status === 401) {
        setReturnTo(window.location.pathname + window.location.search);
        toast.message("Please sign in to continue");
        navigate("/login", { state: { authRequired: true } });
      } else {
        toast.error(partnerCheckoutErrorMessage(detail));
      }
    } finally { setSubmitting(false); }
  };

  const onKycApproved = async () => { setKycOpen(false); await checkAuth?.(); submit(); };

  const payLabel = stripeConfigured && total >= 0.5
    ? `Pay ${formatMoney(total, currency)} with Stripe`
    : `Book now · ${formatMoney(total, currency)}`;

  if (confirmed) {
    return (
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20" data-testid="ext-confirmed">
        <div className="zc-card p-6 sm:p-10 text-center">
          <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
            <CheckCircle2 size={24} className="text-[var(--zc-success)]" />
          </div>
          <div className="eyebrow mt-6">Confirmed</div>
          <h1 className="zc-headline text-4xl mt-2">Your partner trip is booked.</h1>
          <p className="text-[var(--zc-text-2)] mt-3 max-w-md mx-auto">
            {v.make} {v.model} · {formatTripRange(confirmed.start_date, confirmed.end_date) || "—"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Booking ID</div>
              <div className="font-mono text-xs">{confirmed.booking_id}</div>
            </div>
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Total</div>
              <div className="zc-price text-2xl">{formatMoney(confirmed.total, confirmed.currency || currency)}</div>
            </div>
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Status</div>
              <div className="text-sm flex items-center gap-2 mt-1 justify-center"><span className={"dot " + bookingStatusDotClass(confirmed.status)} /> {formatBookingStatus(confirmed)}</div>
            </div>
          </div>

          <div className="mt-10 zc-card p-5 text-left text-sm text-[var(--zc-text-2)] leading-relaxed" style={{ background: "rgba(22,163,74,0.06)", borderColor: "rgba(22,163,74,0.25)" }}>
            <div className="font-semibold text-[var(--zc-text)] mb-1">What happens next</div>
            Your reservation with {confirmed.vehicle_snapshot?.supplier || v.owner_name || "our partner"} is confirmed on Z-Cars.
            Pick-up details and any partner-specific instructions will be in your trip summary and email.
            <span className="block mt-2 text-[var(--zc-text-3)]">A confirmation email has been sent to your account email.</span>
          </div>

          <div className="flex justify-center gap-3 mt-8">
            <Link to="/profile/bookings" className="px-5 h-11 grid place-items-center zc-btn-primary text-sm" data-testid="ext-go-trips">View my trips</Link>
            <Link to="/browse" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm" data-testid="ext-keep-browsing">Keep browsing</Link>
          </div>
        </div>

        {confirmedEsim && (
          <div className="mt-8 text-left" data-testid="ext-esim-block">
            <div className="eyebrow mb-3">Travel Pack · eSIM</div>
            <EsimCard esim={confirmedEsim} onUpdate={async () => {
              try {
                const er = await axios.get(`${API}/esim/mine`, { withCredentials: true });
                const refreshed = pageItems(er.data).find((e) => e.activation_id === confirmedEsim.activation_id);
                if (refreshed) setConfirmedEsim(refreshed);
              } catch (_) {}
            }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="ext-details-page">
      <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm text-[var(--zc-text-3)] hover:text-[var(--zc-text)] mb-6">
        <ArrowLeft size={14} /> Back to fleet
      </Link>

      {paymentCancelled && (
        <div
          className="mb-6 p-4 rounded-lg border flex items-start gap-3"
          style={{ background: "rgba(245,158,11,0.1)", borderColor: "rgba(245,158,11,0.45)" }}
          data-testid="ext-stripe-cancel-banner"
        >
          <AlertTriangle size={18} className="text-[var(--zc-warn)] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold text-[var(--zc-text)]">Payment cancelled</div>
            <p className="text-sm text-[var(--zc-text-2)] mt-1">
              Adjust your trip details below and try payment again, or use test-card checkout if Stripe is unavailable.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1.5fr,1fr] gap-10">
        {/* LEFT: hero */}
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="zc-badge bg-[var(--zc-bg-3)] text-[var(--zc-text)]">
              {v.vehicle_type}
            </span>
            <span className="zc-badge flex items-center gap-1 text-white" style={{ background: "rgba(15,15,18,0.85)" }}>
              <Globe2 size={9} /> Partner · {v.external_source || "Live"}
            </span>
          </div>
          <h1 className="zc-headline text-4xl md:text-5xl tracking-tight">{v.make} {v.model}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[var(--zc-text-2)]">
            <span className="flex items-center gap-1"><Star size={13} className="text-[var(--zc-accent)]" fill="currentColor" /> {v.avg_rating?.toFixed?.(1) || "—"} <span className="text-[var(--zc-text-3)]">({v.rating_count || 0} trips)</span></span>
            <span className="flex items-center gap-1"><MapPin size={13} /> {v.location_city}</span>
            <span className="flex items-center gap-1"><Users size={13} /> {v.seats} seats</span>
            <span className="flex items-center gap-1"><SettingsIcon size={13} /> {v.transmission}</span>
          </div>

          {img ? (
            <div className="aspect-[16/10] rounded-2xl overflow-hidden mt-6 bg-[var(--zc-bg-3)]">
              <img src={img} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-[16/10] rounded-2xl mt-6 bg-[var(--zc-bg-3)] grid place-items-center text-[var(--zc-text-3)]">
              No image available
            </div>
          )}

          {v.description && (
            <div className="mt-8">
              <div className="eyebrow mb-2">About this vehicle</div>
              <p className="text-[var(--zc-text-2)] leading-relaxed">{v.description}</p>
            </div>
          )}

          {v.features?.length > 0 && (
            <div className="mt-8">
              <div className="eyebrow mb-3">Features</div>
              <div className="flex flex-wrap gap-2">
                {v.features.map((f) => (
                  <span key={f} className="text-xs px-3 h-7 grid place-items-center rounded-full bg-[var(--zc-bg-3)] text-[var(--zc-text)]">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="zc-card p-5 mt-8 flex items-start gap-4" style={{ background: "rgba(37,99,235,0.05)", borderColor: "rgba(37,99,235,0.2)" }}>
            <Globe2 size={18} className="text-[var(--zc-secondary)] shrink-0 mt-0.5" />
            <div className="text-sm text-[var(--zc-text)] leading-relaxed">
              <strong>Partner fleet.</strong> This vehicle is supplied by {v.owner_name || "an external partner"}.
              When you book, you pay through Stripe (or test checkout in dev) at the quoted rate before your trip is confirmed.
            </div>
          </div>
        </div>

        {/* RIGHT: booking sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="zc-card p-6">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-xs text-[var(--zc-text-3)] uppercase tracking-widest">Daily rate</div>
                <div className="zc-price text-4xl mt-1 zc-price-now">${v.daily_rate}</div>
              </div>
              <div className="text-xs text-[var(--zc-text-3)] uppercase tracking-widest">/day</div>
            </div>
            <div className="text-xs text-[var(--zc-text-2)] mb-5 pb-5 border-b border-[var(--zc-border)]">
              Listed by <span className="font-semibold text-[var(--zc-text)]">{v.owner_name || "Partner"}</span>
            </div>

            <div className="space-y-4">
              <div>
                <div className="eyebrow mb-2">Pick-up</div>
                <SplitDateTime
                  dateLabel="Pick-up date" timeLabel="Pick-up time"
                  value={start} onChange={setStart}
                  testidPrefix="ext-pickup"
                />
              </div>
              <div>
                <div className="eyebrow mb-2">Drop-off</div>
                <SplitDateTime
                  dateLabel="Drop-off date" timeLabel="Drop-off time"
                  value={end} onChange={setEnd}
                  minDate={start || undefined}
                  testidPrefix="ext-dropoff"
                />
              </div>

              <div>
                <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 flex items-center gap-1.5">
                  <MapPin size={11} /> Pick-up location
                </label>
                <input
                  type="text" value={pickupLoc} onChange={(e) => setPickupLoc(e.target.value)}
                  className="zc-input h-11 px-3 w-full text-sm"
                  data-testid="ext-pickup-loc"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={oneWay} onChange={(e) => setOneWay(e.target.checked)} data-testid="ext-oneway" />
                  <span>Different drop-off location <span className="text-[var(--zc-text-3)]">(+$30)</span></span>
                </label>
                {oneWay && (
                  <input
                    type="text" value={dropoffLoc} onChange={(e) => setDropoffLoc(e.target.value)}
                    placeholder="Drop-off address"
                    className="zc-input h-11 px-3 w-full text-sm mt-2"
                    data-testid="ext-dropoff-loc"
                  />
                )}
              </div>

              <div>
                <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 flex items-center gap-1.5">
                  <Phone size={11} /> Contact phone <span className="text-[var(--zc-text-3)]">(optional)</span>
                </label>
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 010 0000"
                  className="zc-input h-11 px-3 w-full text-sm"
                  data-testid="ext-phone"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 flex items-center gap-1.5">
                  <Pencil size={11} /> Notes for the partner <span className="text-[var(--zc-text-3)]">(optional)</span>
                </label>
                <textarea
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Child seat, late pickup, etc."
                  rows={2}
                  className="zc-input px-3 py-2 w-full text-sm"
                  data-testid="ext-notes"
                />
              </div>
            </div>

            <div className="mt-5">
              {airaloReady === false && (
                <p className="zc-hint mb-2">Travel Pack is temporarily unavailable.</p>
              )}
              <TravelPackToggle
                checked={addEsim}
                onToggle={setAddEsim}
                testid="ext-travel-pack"
                disabled={airaloReady === false}
              />
            </div>

            {/* Estimate */}
            <div className="mt-5 pt-5 border-t border-[var(--zc-border)] space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[var(--zc-text-2)]">${v.daily_rate} × {days || 0} days</span><span>${formatUsd(subtotal)}</span></div>
              {oneWayFee > 0 && (
                <div className="flex justify-between"><span className="text-[var(--zc-text-2)]">One-way fee</span><span>${formatUsd(oneWayFee)}</span></div>
              )}
              <div className="flex justify-between zc-title text-xl pt-2 mt-2 border-t border-[var(--zc-border)]">
                <span>Total</span><span>{formatMoney(total, currency)}</span>
              </div>
              {apiTripTotal != null && apiTripDays != null && days !== apiTripDays && (
                <div className="zc-hint">
                  Search quote was for {apiTripDays} days ({formatMoney(apiTripTotal, currency)}); your dates use {days} days.
                </div>
              )}
              <div className="zc-hint">
                {stripeConfigured && total >= 0.5 ? "Pay securely with Stripe at checkout" : "Confirmed at checkout"} · {currency}
              </div>
            </div>

            <button
              onClick={submit}
              disabled={submitting || days < 1 || !pickupLoc.trim()}
              className="w-full h-12 zc-btn-primary text-sm mt-5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="ext-reserve-cta"
            >
              {submitting ? "Processing…" : stripeConfigured && total >= 0.5 ? <><CreditCard size={14} /> {payLabel}</> : payLabel}
            </button>

            <Link
              to="/browse"
              className="inline-flex items-center justify-center w-full h-11 zc-btn-ghost text-xs mt-3"
              data-testid="ext-see-hosted"
            >
              See bookable Z-Cars vehicles
            </Link>
          </div>
        </aside>
      </div>
      <KycModal open={kycOpen} onClose={() => setKycOpen(false)} onApproved={onKycApproved} />
    </div>
  );
}
